/**
 * List View Content Service
 *
 * Single source of truth for what the listing sheet panel shows: whether the Floor Plans /
 * Quick Move-Ins tab bar renders, and how to render the panel's content on mount, chip switch,
 * tab switch, or house-plans/housing-units data changes. PANEL_CONTENT is keyed purely by active
 * chip — one concern, one namespace. A chip whose content has a full-screen "expanded" variant
 * (currently just Sold, via VIEW_SOLD_FULL_LIST) declares it as a nested `fullListContent` on
 * that chip's own entry, rather than as a second, differently-typed key living alongside the
 * chip keys — see getListViewContent. Mirrors services/listingTitleService.js's per-chip lookup
 * pattern — add a new chip's panel behavior here instead of branching on it inside
 * plugins/listView.plugin.js.
 *
 * @module nativeInteractiveSiteMap/services/listViewContentService
 */

import LotStatus from '../enums/lotStatus';
import { renderFloorPlanGrid, renderFloorPlanCard } from '../components/floorPlanCards.dom';
import renderComingSoonPanel from '../components/comingSoonPanel.dom';
import { renderQuickMoveInGrid, renderQuickMoveInCard } from '../components/quickMoveInCards.dom';
import renderUnavailableLotsPanel from '../components/unavailableLots.dom';
import renderSoldHomeCard from '../components/soldHomeCard.dom';
import renderSimilarHomesSection from '../components/similarHomesSection.dom';
import findSimilarHomes from './similarHomesService';
import {
  selectSelectedTab,
  selectFilteredHousePlans,
  selectFilteredHousingUnits,
  selectSelectedLotHousePlanIds,
  selectSoldHomesWithSelectedFirst,
  selectSimilarHomesCandidates,
  selectActiveView,
  selectActiveChip,
  selectSoldLots,
  selectLotsById,
} from '../redux/selectors';
import { TAB_FLOOR_PLANS, TAB_QUICK_MOVE_INS, VIEW_SOLD_FULL_LIST, MAX_SOLD_HOMES_MOBILE } from '../constants';

/**
 * Creates a renderer wrapper that displays an empty state panel when no cards are available,
 * otherwise delegates to the provided grid renderer.
 *
 * @param {Function} gridRenderer - The grid renderer function (e.g., renderFloorPlanGrid)
 * @returns {Function} A renderer function that accepts cards array and returns HTML
 */
function createGridRendererWithEmptyState(gridRenderer) {
  return (cards) => {
    if (!Array.isArray(cards) || cards.length === 0) {
      return renderUnavailableLotsPanel();
    }
    return gridRenderer(cards);
  };
}

function renderAvailableContent(storeState, { renderCards }) {
  if (selectSelectedTab(storeState) === TAB_QUICK_MOVE_INS) {
    renderCards(
      selectFilteredHousingUnits(storeState),
      createGridRendererWithEmptyState((cards) => renderQuickMoveInGrid(cards))
    );
    return;
  }
  renderCards(
    selectFilteredHousePlans(storeState),
    createGridRendererWithEmptyState((cards) => renderFloorPlanGrid(cards))
  );
}

function renderAvailableOnHousePlansChanged(storeState, renderContext) {
  if (selectSelectedTab(storeState) !== TAB_FLOOR_PLANS) {
    return;
  }
  renderAvailableContent(storeState, renderContext);
}

function renderAvailableOnHousingUnitsChanged(storeState, renderContext) {
  if (selectSelectedTab(storeState) !== TAB_QUICK_MOVE_INS) {
    return;
  }
  renderAvailableContent(storeState, renderContext);
}

/**
 * Model lots can point at either a buildable plan (housePlanIds) or a spec home
 * (housingUnitId) — see redux/selectors.js#selectFilteredHousingUnits/#selectFilteredHousePlans.
 * A community can have both kinds of model lots at once, so this grid renders the union of the
 * two chip-filtered lists, picking the matching card renderer per item.
 */
function renderModelGrid(cards) {
  const cardsHtml = cards
    .map((card) => (card.specificationId != null ? renderQuickMoveInCard(card) : renderFloorPlanCard(card)))
    .join('');
  const gridClasses = [
    'nhs-c-native-interactive-site-map__card-grid',
    'nhs-mq-native-interactive-site-map__card-grid-medium',
    'nhs-mq-native-interactive-site-map__card-grid-large',
  ];

  if (cards.length > 1) {
    gridClasses.push('nhs-c-native-interactive-site-map__card-grid--scroll');
  }

  return `
    <div class="${gridClasses.join(' ')}" data-qa="native_ism_model_grid">
      ${cardsHtml}
    </div>
  `;
}

// A Model home plan corresponds to exactly one lot (see floorPlanCardSelectedEpic.js), so its
// card shows that lot's number instead of the generic Available-tab "N Lots available" badge.
function withModelLotDisplayLabel(plan, lotsById) {
  const lotDisplayLabel = plan.lotIds.length === 1 ? lotsById[plan.lotIds[0]]?.displayLabel ?? null : null;
  return { ...plan, lotDisplayLabel };
}

function renderModelContent(storeState, { renderCards }) {
  const lotsById = selectLotsById(storeState);
  const housePlanCards = selectFilteredHousePlans(storeState)
    .map((plan) => withModelLotDisplayLabel(plan, lotsById));
  renderCards(
    [...housePlanCards, ...selectFilteredHousingUnits(storeState)],
    createGridRendererWithEmptyState(renderModelGrid)
  );
}

function renderComingSoonContent(storeState, { renderCards, community }) {
  const priorityPlanIds = selectSelectedLotHousePlanIds(storeState);
  renderCards(
    selectFilteredHousePlans(storeState),
    createGridRendererWithEmptyState((cards) => renderComingSoonPanel(cards, {
      priorityPlanIds,
      community,
    }))
  );
}

function renderDefaultContent(storeState, { renderCards }) {
  renderCards(
    selectFilteredHousePlans(storeState),
    createGridRendererWithEmptyState((cards) => renderFloorPlanGrid(cards))
  );
}

/**
 * Wraps each sold home's card + similar-homes section in a shared container (Figma's "Sold Card /
 * Master" — one rounded, backgrounded card enclosing both pieces, not two separate cards). Only
 * the first group in the sequence starts with its similar-homes section expanded — same rule for
 * mobile's uncapped "See All" list and desktop's capped Listing view, since they both funnel
 * through this one function. The one exception is `isMobile` capped-Listing rendering (`scrollable`
 * *and* `isMobile` both true): since that carousel only ever shows one card at a time (see below),
 * every card starts expanded there instead — otherwise swiping to the next card would take an
 * extra tap just to see the same section the previous card already had open. `scrollable` alone
 * (desktop's capped Listing render also sets it, see renderSoldContent) doesn't trigger this —
 * only the mobile viewport actually shows one card at a time; desktop still shows the vertical
 * stack the CSS modifier reverts to at the `medium` breakpoint (see below), so it keeps the
 * first-only default.
 *
 * `scrollable` (only ever passed by renderSoldContent, the capped Listing view) adds the
 * mobile-first horizontal scroll-snap modifier — same architecture as
 * components/floorPlanCards.dom.js's `--scroll`/`nhs-mq-*-medium` pair: mobile shows one card at
 * a time with the next peeking at the edge, and the `-medium` class reverts to the plain vertical
 * stack at the `medium` breakpoint and up. renderSoldFullListContent never passes it, so
 * VIEW_SOLD_FULL_LIST stays a vertical stack (and the first-card-only-expanded default) at every
 * breakpoint.
 *
 * Carries `data-lot-id` (the Sold lot's numeric `lotId`, joined here against `soldLots` via
 * `sitePlanLotId` since selectSoldHomes doesn't project `lotId` itself) so
 * plugins/tracking.plugin.js can resolve which sold-home card a Similar Homes interaction
 * happened in directly from the DOM, rather than from `selectedLotId` (NHS20-8250) — the Sold
 * listing renders every sold home's card+section at once, independent of map selection. Sourced
 * from selectSoldLots rather than selectLots/AvailableLots: sold-status lots aren't guaranteed to
 * appear there (see redux/selectors.js#selectSoldLots), but every entry in selectSoldLots carries
 * its own `lotId` directly from the SoldLots API section (services/processLotMapInventory.js).
 */
function renderSoldHomeCards(homes, candidates, soldLots, { scrollable = false, isMobile = false } = {}) {
  const lotIdBySitePlanLotId = new Map(soldLots.map((lot) => [lot.sitePlanLotId, lot.lotId]));
  const allExpanded = scrollable && isMobile;
  const cardsHtml = homes
    .map((home, index) => `
      <div class="nhs-c-native-interactive-site-map__sold-card-group" data-sold-card-group data-lot-id="${lotIdBySitePlanLotId.get(home.sitePlanLotId) ?? ''}" data-qa="native_ism_sold_card_group">
        ${renderSoldHomeCard(home)}
        ${renderSimilarHomesSection(findSimilarHomes(home, candidates), { expanded: allExpanded || index === 0 })}
      </div>
    `)
    .join('');

  const listClasses = ['nhs-c-native-interactive-site-map__sold-list'];
  const isCarousel = scrollable && homes.length > 1;
  if (isCarousel) {
    listClasses.push(
      'nhs-c-native-interactive-site-map__sold-list--scroll',
      'nhs-mq-native-interactive-site-map__sold-list-medium'
    );
  }

  // data-sold-card-carousel / data-sold-card-group are the JS hooks
  // handlers/soldCardCarouselHandlers.js looks these up by, rather than the styling classes above.
  return `
    <div class="${listClasses.join(' ')}"${isCarousel ? ' data-sold-card-carousel' : ''} data-qa="native_ism_sold_list">
      ${cardsHtml}
    </div>
  `;
}

/**
 * Sold listing content (NHS20-8825/8826/8828): the full Sold Home Card listing sequence
 * (selectSoldHomes), with whichever sold lot is currently selected promoted to the front
 * (NHS20-8828). Each card carries its own "Similar Homes Available" section (NHS20-8827: "at the
 * bottom of EACH Sold Home Card") — expand/collapse behavior (only the first expanded by
 * default) is a presentation concern for whoever styles this, not this pipeline. Sold lots with
 * no house plan resolved (see redux/selectors.js#selectSoldHomes) are simply absent from the
 * list rather than rendered as broken cards — selecting one just leaves the rest of the sequence
 * showing instead of going blank.
 *
 * On mobile, only the first MAX_SOLD_HOMES_MOBILE render here. The See All button itself lives in
 * the header (components/listingHeader.dom.js), not in this scrollable content — reachable
 * without scrolling — so plugins/listView.plugin.js shows/hides it independently of this
 * function; it isn't part of the HTML returned here.
 */
function renderSoldContent(storeState, { renderCards, isMobile }) {
  const soldHomes = selectSoldHomesWithSelectedFirst(storeState);
  const candidates = selectSimilarHomesCandidates(storeState);
  const soldLots = selectSoldLots(storeState);
  const visibleHomes = isMobile ? soldHomes.slice(0, MAX_SOLD_HOMES_MOBILE) : soldHomes;

  renderCards(
    soldHomes,
    createGridRendererWithEmptyState(() =>
      renderSoldHomeCards(visibleHomes, candidates, soldLots, { scrollable: true, isMobile }))
  );
}

/**
 * The Sold Full List view's content (VIEW_SOLD_FULL_LIST, NHS20-8826's "Mobile Full Sold State
 * View") — every sold home, uncapped, selected lot still promoted first. Same renderCards-based
 * shape as every other descriptor below, so getListViewContent can hand it back for any of the
 * four render* hooks without plugins/listView.plugin.js needing to know it's not chip-based.
 */
function renderSoldFullListContent(storeState, { renderCards }) {
  const soldHomes = selectSoldHomesWithSelectedFirst(storeState);
  const candidates = selectSimilarHomesCandidates(storeState);
  const soldLots = selectSoldLots(storeState);

  renderCards(
    soldHomes,
    createGridRendererWithEmptyState(() => renderSoldHomeCards(soldHomes, candidates, soldLots))
  );
}

/**
 * Every chip's panel descriptor, keyed purely by active-chip key (see module doc for why
 * VIEW_SOLD_FULL_LIST isn't a peer key here).
 */
const PANEL_CONTENT = {
  [LotStatus.Available.toString()]: {
    hasTabs: true,
    renderContent: renderAvailableContent,
    renderOnHousePlansChanged: renderAvailableOnHousePlansChanged,
    renderOnHousingUnitsChanged: renderAvailableOnHousingUnitsChanged,
    renderOnSelectedLotChanged: () => {},
  },
  [LotStatus.ComingSoon.toString()]: {
    hasTabs: false,
    renderContent: renderComingSoonContent,
    renderOnHousePlansChanged: renderComingSoonContent,
    renderOnHousingUnitsChanged: () => {},
    renderOnSelectedLotChanged: renderComingSoonContent,
  },
  [LotStatus.Sold.toString()]: {
    hasTabs: false,
    renderContent: renderSoldContent,
    // Both wired to renderSoldContent (not just housing units) — selectSimilarHomesCandidates
    // draws candidates from both housePlans and housingUnits, so either one arriving/changing
    // can change the Similar Homes Available result for whatever sold home is selected.
    renderOnHousePlansChanged: renderSoldContent,
    renderOnHousingUnitsChanged: renderSoldContent,
    renderOnSelectedLotChanged: renderSoldContent,
    // The uncapped "See All" destination (NHS20-8826) — same chip, different activeView. Nested
    // here (not a top-level PANEL_CONTENT entry) because it only ever applies while Sold is the
    // active chip; getListViewContent below swaps to it when activeView matches.
    fullListContent: {
      activeView: VIEW_SOLD_FULL_LIST,
      hasTabs: false,
      renderContent: renderSoldFullListContent,
      renderOnHousePlansChanged: renderSoldFullListContent,
      renderOnHousingUnitsChanged: renderSoldFullListContent,
      renderOnSelectedLotChanged: renderSoldFullListContent,
    },
  },
  [LotStatus.Model.toString()]: {
    hasTabs: false,
    renderContent: renderModelContent,
    renderOnHousePlansChanged: renderModelContent,
    renderOnHousingUnitsChanged: renderModelContent,
    renderOnSelectedLotChanged: () => {},
  },
};

/**
 * Fallback for chips without dedicated panel content yet (e.g. Sold): plain floor-plan
 * grid, no tabs, no chip-specific UI. Matches the plugin's pre-existing behavior for those chips.
 */
const DEFAULT_CONTENT = {
  hasTabs: false,
  renderContent: renderDefaultContent,
  renderOnHousePlansChanged: renderDefaultContent,
  renderOnHousingUnitsChanged: () => {},
  renderOnSelectedLotChanged: () => {},
};

/**
 * Looks up the active chip's descriptor, then swaps to its nested fullListContent (if it has
 * one) when activeView currently matches that variant's own activeView — e.g. Sold's
 * fullListContent.activeView is VIEW_SOLD_FULL_LIST, so this returns that variant instead of
 * Sold's regular one exactly when the user is on the "See All" screen.
 *
 * @typedef {{ renderCards: Function, isMobile: boolean, community: Object }} RenderContext
 * @param {object} storeState
 * @returns {{
 *   hasTabs: boolean,
 *   renderContent: (storeState: object, renderContext: RenderContext) => void,
 *   renderOnHousePlansChanged: (storeState: object, renderContext: RenderContext) => void,
 *   renderOnHousingUnitsChanged: (storeState: object, renderContext: RenderContext) => void,
 *   renderOnSelectedLotChanged: (storeState: object, renderContext: RenderContext) => void,
 * }}
 */
export default function getListViewContent(storeState) {
  const chipContent = PANEL_CONTENT[selectActiveChip(storeState)] ?? DEFAULT_CONTENT;
  const { fullListContent } = chipContent;

  if (fullListContent && selectActiveView(storeState) === fullListContent.activeView) {
    return fullListContent;
  }
  return chipContent;
}
